import { defineStoryblok, resolveId } from "@trydig/storyblok";
import type { ISbStoryData } from "storyblok-js-client";
import { rpc } from "#rpc";
import { AccordionSectionBlok } from "@/components/bloks/AccordionSectionBlok";
import { AccordionSectionGroupBlok } from "@/components/bloks/AccordionSectionGroupBlok";
import {
	ArticleListingPageBlok,
	ArticleListingPageSkeleton,
} from "@/components/bloks/ArticleListingPageBlok";
import { ArticlePageBlok } from "@/components/bloks/ArticlePageBlok";
import { ArticleSectionBlok } from "@/components/bloks/ArticleSectionBlok";
import { CategoryCardBlok } from "@/components/bloks/CategoryCardBlok";
import { CustomerServiceBlok } from "@/components/bloks/CustomerServiceBlok";
import { CustomerServiceTopicBlok } from "@/components/bloks/CustomerServiceTopicBlok";
import { EmployeeBlok } from "@/components/bloks/EmployeeBlok";
import { ExplorationCategoryBlok } from "@/components/bloks/ExplorationCategoryBlok";
import {
	ExplorationProductBlok,
	explorationProductFetcher,
	explorationProductKey,
} from "@/components/bloks/ExplorationProductBlok";
import {
	ExplorationProductInContentBlok,
	explorationProductInContentFetcher,
	explorationProductInContentKey,
} from "@/components/bloks/ExplorationProductInContentBlok";
import {
	FaqDetailsPageBlok,
	FaqDetailsPageBlokSkeleton,
} from "@/components/bloks/FaqDetailsPageBlok";
import { FaqGroupBlok } from "@/components/bloks/FaqGroupBlok";
import { FaqListingPageBlok } from "@/components/bloks/FaqListingPageBlok";
import { FeaturedCollectionAssetBlok } from "@/components/bloks/FeaturedCollectionAssetBlok";
import {
	FeaturedCollectionHighlightedBlok,
	featuredCollectionHighlightedFetcher,
	featuredCollectionHighlightedKey,
} from "@/components/bloks/FeaturedCollectionHighlightedBlok";
import {
	FeaturedCollectionProductsBlok,
	featuredCollectionProductsFetcher,
	featuredCollectionProductsKey,
} from "@/components/bloks/FeaturedCollectionProductsBlok";
import { FooterMarqueeItemBlok } from "@/components/bloks/FooterMarqueeItemBlok";
import { FooterSectionBlok } from "@/components/bloks/FooterSectionBlok";
import { HeroBlok } from "@/components/bloks/HeroBlok";
import { LinkBlok } from "@/components/bloks/LinkBlok";
import { DualMediaBlok } from "@/components/bloks/Media/DualMediaBlok";
import { DualMediaTextBlok } from "@/components/bloks/Media/DualMediaTextBlok";
import { MediaBlok } from "@/components/bloks/Media/MediaBlok";
import { MegaMenuBlok } from "@/components/bloks/MegaMenuBlok";
import { MegaMenuSectionBlok } from "@/components/bloks/MegaMenuSectionBlok";
import {
	MembersClubFormBlok,
	type MembersClubFormStoryblok,
} from "@/components/bloks/MembersClubForm";
import { PageBlok } from "@/components/bloks/PageBlok";
import { PageHeaderBlok } from "@/components/bloks/PageHeaderBlok";
import { PageHeaderSubCategoryBlok } from "@/components/bloks/PageHeaderSubCategoryBlok";
import { PhoneLinkBlok } from "@/components/bloks/PhoneLinkBlok";
import { ProductCollectionBlok } from "@/components/bloks/ProductCollectionBlok";
import { PageHeaderCollectionBlok } from "@/components/bloks/ProductHeaderCollectionBlok";
import { ProductListingCategoryCardBlok } from "@/components/bloks/ProductListingCategoryCardBlok";
import {
	ProductListingPageBlok,
	ProductListingPageBlokSkeleton,
	productListingPageFetcher,
	productListingPageKey,
} from "@/components/bloks/ProductListingPageBlok";
import { ReclaimitFormBlok } from "@/components/bloks/ReclaimitFormBlok";
import { ReusableBlok } from "@/components/bloks/ReusableBlok";
import { ReusablePreviewBlok } from "@/components/bloks/ReusablePreviewBlok";
import { SectionHighlightBlok } from "@/components/bloks/SectionHighlightBlok";
import { SectionHighlightItemBlok } from "@/components/bloks/SectionHighlightItemBlok";
import { StoresSectionBlok } from "@/components/bloks/StoresSectionBlok";
import { TextBlok } from "@/components/bloks/TextBlok";
import { TextSecondaryBlok } from "@/components/bloks/TextSecondaryBlok";
import type { TypescriptGeneratorStoryblok } from "@/generated/sb/sb-types";

// `membersClubForm` is registered in Storyblok but not yet in the generated
// types — extend the union here until `pnpm generate:storyblok` is run.
export type AllBloks =
	| NonNullable<TypescriptGeneratorStoryblok["body"]>[number]
	| MembersClubFormStoryblok;

const NotFound = () => {
	return null;
};

defineStoryblok<AllBloks>({
	bloks: {
		articleListingPage: {
			blok: ArticleListingPageBlok,
			fetcher: rpc.articles.list,
			getKey: ({ blok }) =>
				(blok.featuredStories || [])
					.map((item) => resolveId(item as ISbStoryData<unknown>))
					.filter((id): id is string => Boolean(id)),
			fallback: ArticleListingPageSkeleton,
		},
		articleSection: {
			blok: ArticleSectionBlok,
			fetcher: rpc.stories.articles,
			getKey: ({ blok }) => blok?._uid,
		},
		explorationProduct: {
			blok: ExplorationProductBlok,
			fetcher: explorationProductFetcher,
			getKey: explorationProductKey,
		},
		explorationProductInContent: {
			blok: ExplorationProductInContentBlok,
			fetcher: explorationProductInContentFetcher,
			getKey: explorationProductInContentKey,
		},
		faqDetailsPage: {
			blok: FaqDetailsPageBlok,
			fetcher: rpc.stories.faq,
			fallback: FaqDetailsPageBlokSkeleton,
		},
		featuredCollectionHighlighted: {
			blok: FeaturedCollectionHighlightedBlok,
			fetcher: featuredCollectionHighlightedFetcher,
			getKey: featuredCollectionHighlightedKey,
		},
		featuredCollectionProducts: {
			blok: FeaturedCollectionProductsBlok,
			fetcher: featuredCollectionProductsFetcher,
			getKey: featuredCollectionProductsKey,
		},
		reusableBlok: {
			blok: ReusableBlok,
			fetcher: rpc.stories.reusable,
			getKey: ({ blok }) =>
				(blok.target || [])
					.map((item) => resolveId(item as ISbStoryData<unknown>))
					.filter((id): id is string => Boolean(id)),
		},
		storesSection: {
			blok: StoresSectionBlok,
			fetcher: rpc.stories.storesSection,
			getKey: ({ blok }) => blok?._uid,
		},
		productListingPage: {
			blok: ProductListingPageBlok,
			fetcher: productListingPageFetcher,
			getKey: productListingPageKey,
			fallback: ProductListingPageBlokSkeleton,
		},

		accordionSection: AccordionSectionBlok,
		accordionSectionGroup: AccordionSectionGroupBlok,
		articlePage: ArticlePageBlok,
		categoryCard: CategoryCardBlok,
		customerService: CustomerServiceBlok,
		customerServiceTopic: CustomerServiceTopicBlok,
		dualMedia: DualMediaBlok,
		dualMediaText: DualMediaTextBlok,
		employee: EmployeeBlok,
		explorationCategory: ExplorationCategoryBlok,
		faqGroup: FaqGroupBlok,
		faqListingPage: FaqListingPageBlok,
		featuredCollectionAsset: FeaturedCollectionAssetBlok,
		footerMarqueeItem: FooterMarqueeItemBlok,
		footerSection: FooterSectionBlok,
		hero: HeroBlok,
		link: LinkBlok,
		media: MediaBlok,
		megaMenu: MegaMenuBlok,
		megaMenuSection: MegaMenuSectionBlok,
		membersClubForm: MembersClubFormBlok,
		page: PageBlok,
		pageHeader: PageHeaderBlok,
		pageHeaderCollection: PageHeaderCollectionBlok,
		pageHeaderSubcategory: PageHeaderSubCategoryBlok,
		phoneLink: PhoneLinkBlok,
		productCollection: ProductCollectionBlok,
		productListingCategoryCard: ProductListingCategoryCardBlok,
		reclaimitForm: ReclaimitFormBlok,
		reusablePreview: ReusablePreviewBlok,
		sectionHighlight: SectionHighlightBlok,
		sectionHighlightItem: SectionHighlightItemBlok,
		text: TextBlok,
		textSecondary: TextSecondaryBlok,

		footer: NotFound,
		global: NotFound,
		header: NotFound,
		productDetailsPage: NotFound,
		snackbar: NotFound,
		store: NotFound,
		TYPESCRIPT_GENERATOR: NotFound,
	},
});
